import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { P2pStats } from '@/hooks/useHlsPlayer';

const DISMISS_KEY = 'cobstream:player:p2pBadgeDismissed';

function readDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try { return window.sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
}
function writeDismissed(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.sessionStorage.setItem(DISMISS_KEY, '1');
    else window.sessionStorage.removeItem(DISMISS_KEY);
  } catch { /* storage disabled */ }
}

function formatPct(stats: P2pStats): number {
  const total = stats.bytesFromPeers + stats.bytesFromOrigin;
  if (total === 0) return 0;
  return Math.round((stats.bytesFromPeers / total) * 100);
}

interface Props {
  stats: P2pStats;
  /** When false, render nothing (i.e. P2P is not active for this player). */
  active: boolean;
}

/**
 * Tiny "P2P · N peers · X%" badge shown below the player when P2P is active.
 * Honest: tells the user they're sharing bandwidth and how much. Dismissable
 * per session via the × button; the pref is sessionStorage-scoped so a fresh
 * tab re-shows it.
 */
export function P2pBadge({ stats, active }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());

  useEffect(() => {
    function onStorage(e: StorageEvent): void {
      if (e.key !== DISMISS_KEY) return;
      setDismissed(readDismissed());
    }
    window.addEventListener('storage', onStorage);
    return (): void => { window.removeEventListener('storage', onStorage); };
  }, []);

  if (!active || dismissed) return null;

  const pct = formatPct(stats);
  return (
    <div className="p2p-badge" role="status" aria-live="polite" data-testid="p2p-badge">
      <span className="p2p-badge__dot" data-state={stats.peers > 0 ? 'peers' : 'solo'} aria-hidden="true" />
      <span className="p2p-badge__text">
        <strong>P2P</strong>
        {' · '}
        {stats.peers === 0 ? t('player.p2p.searching') : t('player.p2p.peers', { count: stats.peers, pct })}
      </span>
      <button
        type="button"
        className="p2p-badge__dismiss"
        aria-label={t('player.p2p.dismiss')}
        onClick={(): void => { writeDismissed(true); setDismissed(true); }}
      >
        ×
      </button>
    </div>
  );
}
